// SPDX-License-Identifier: AGPL-3.0-only
import { layoutItemSchema, pageLayoutSchema, queryDescriptorSchema } from '@adminium/widgets/page-config';
import { z } from 'zod';
/**
 * The config envelope every stored config document uses. The envelope
 * persists verbatim into `adminium_pages.config`; the stored version path
 * the config-migration runner keys on is `config.v`.
 */
export const CONFIG_KINDS = ['page', 'dashboard', 'view', 'nav', 'manifest-module'];
export const configKindSchema = z.enum(CONFIG_KINDS);
/**
 * Prefixed document id: `page_`/`view_`/`nav_` + slug or ULID suffix.
 * Engine-generated ids carry ULID suffixes (`page_01HZX…`); hand-authored and
 * seeded documents may use readable slugs (`page_customers`, per the
 * examples), so the suffix alphabet is not restricted to Crockford base32.
 */
export const configIdPattern = /^(page|view|nav)_[A-Za-z0-9][A-Za-z0-9_-]*$/;
const kebabCasePattern = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/;
/** Nav placement block. */
export const navConfigSchema = z.object({
    /**
     * OPTIONAL for one reason only: Studio's pre-2026-08-24 "Hide from sidebar"
     * DELETED this key from stored documents (`mergeEnvelopeMeta` expressed the
     * row's null group by omission), so group-less envelopes exist in every
     * install that ever hid a page — and a validator that refuses them fails
     * the whole document for a field only nav-building reads. Generation always
     * writes it, and today's hide keeps it (`hidden` below carries the state);
     * absence means "hidden, placement not remembered".
     */
    group: z.string().min(1).optional(),
    icon: z.string().min(1), // lucide-react icon name
    order: z.number().int(),
    slug: z.string().regex(kebabCasePattern, 'must be kebab-case').optional(),
    badge: z.string().min(1).optional(),
    /**
     * Generated hidden-from-sidebar default (follow-up): cascade-owned child
     * tables (invoice items, proposal items, …) get a page — routable,
     * palette-searchable, and the source of their parent's record-page tab specs
     * — but not a sidebar row; their home is the parent's record page. `group`
     * stays REQUIRED beside it on purpose: the document remembers where the page
     * belongs, so un-hiding in Studio restores the right group. The row
     * projection (`adminium_pages.nav_group = null`) is what nav-building reads.
     */
    hidden: z.boolean().optional(),
});
/**
 * Per-page gutter override for the inner main section. OMITTED is the
 * common case and means "whatever this template normally uses" — only an
 * explicit value overrides, so the default can be retuned centrally
 * without rewriting stored documents.
 *
 * `none` and `standard` name the two shared choices; the object form is an
 * explicit x/y pair in CSS pixels. Capped at 200px because the control that
 * writes it is a number input, and a page inset by four figures is a support
 * ticket, not a layout.
 */
export const pagePaddingSchema = z.union([
    z.literal('none'),
    z.literal('standard'),
    z.object({
        x: z.number().int().min(0).max(200),
        y: z.number().int().min(0).max(200),
    }),
]);
/**
 * The page's content column, named after the container tokens
 * (`apps/dashboard/src/shell/PageSurface.tsx` holds the px values).
 *
 * A CLOSED UNION rather than a free length: these are the caps the design
 * system defines, and the control that writes it is a select. Absent ⇒ the
 * template's own column, exactly as `padding` works — so every document
 * written before this field still validates and still renders unchanged.
 *
 * `fill` is deliberately NOT joined to this. Width is a taste decision an
 * admin can reasonably make; whether a template resolves its own `h-full`
 * chain is a fact about how that template is built, and an admin toggling it
 * would simply break the page.
 */
export const pageWidthSchema = z.enum(['full', 'narrow', 'content', 'page', 'dash', 'wide']);
const envelopeShape = z.object({
    v: z.literal(1), // config schema version (integer)
    kind: configKindSchema,
    id: z
        .string()
        .regex(configIdPattern, 'must be a prefixed document id (page_* / view_* / nav_*)'),
    template: z.string().regex(kebabCasePattern, 'must be a kebab-case Registry id'),
    title: z.object({ key: z.string().min(1), fallback: z.string().min(1) }),
    // Data-scope defaults for widgets/bindings on this page.
    source: z.object({
        connectionId: z.string().nullable(),
        table: z.string().nullable(), // "public.orders" — primary table for record pages
    }),
    nav: navConfigSchema,
    access: z.object({ minRole: z.string().min(1), permissions: z.array(z.string()) }),
    // Page chrome, not template body — hence a sibling of `config` rather than a
    // key inside it. Optional so every stored document predating it still
    // validates and keeps rendering with its template's own gutter.
    padding: pagePaddingSchema.optional(),
    // Same contract as `padding` above: page chrome, optional, absent ⇒ the
    // template's own column from `pages/surfaceDefaults.ts`.
    width: pageWidthSchema.optional(),
    // Per-template body. Unknown fields are preserved on round-trip
    // (forward compatibility); per-template schemas
    // live in @adminium/widgets/page-config and are applied by kind below.
    // `config.templateVersion` (stamped by composeTemplate from the manifest) is
    // ADVISORY-ONLY until the per-template migrations/ mechanism lands:
    // nothing validates or migrates on it — the migration chain keys solely on
    // the envelope `v` above, and stored pre-stamp documents render fine because
    // layouts are self-contained. The intent — "version bumps let a migration
    // step change old configs deliberately" — has no enforcement surface yet.
    config: z.record(z.string(), z.unknown()),
});
export const pageEnvelopeSchema = envelopeShape.superRefine((doc, ctx) => {
    if (doc.kind !== 'dashboard')
        return;
    const layout = pageLayoutSchema.safeParse(doc.config['layout']);
    if (layout.success)
        return;
    for (const issue of layout.error.issues) {
        ctx.addIssue({
            code: 'custom',
            message: issue.message,
            path: ['config', 'layout', ...issue.path],
        });
    }
});
/**
 * A validated dashboard widget instance: a grid layout item whose
 * `config.binding`, when present, must be a declarative query descriptor.
 */
export const widgetConfigSchema = layoutItemSchema.superRefine((item, ctx) => {
    const binding = item.config['binding'];
    if (binding === undefined)
        return;
    const descriptor = queryDescriptorSchema.safeParse(binding);
    if (descriptor.success)
        return;
    for (const issue of descriptor.error.issues) {
        ctx.addIssue({
            code: 'custom',
            message: issue.message,
            path: ['config', 'binding', ...issue.path],
        });
    }
});
//# sourceMappingURL=envelope.js.map